import React, { useState, useEffect } from 'react';
import ApiClient from '../utils/ApiClient';
import SummaryCards from '../components/BusinessInsights/SummaryCards';
import RestockAlerts from '../components/BusinessInsights/RestockAlerts';
import ExcessInventory from '../components/BusinessInsights/ExcessInventory';
import HighReturns from '../components/BusinessInsights/HighReturns';
import BuyboxStatus from '../components/BusinessInsights/BuyboxStatus';
import MarketplaceProsBattle from '../components/BusinessInsights/MarketplaceProsBattle';
import TopPriorities from '../components/BusinessInsights/TopPriorities';

const tabs = [
    { id: 'restock', label: 'Restock Alerts' },
    { id: 'excess', label: 'Excess Inventory' },
    { id: 'returns', label: 'High Returns' },
    { id: 'buybox', label: 'BB Status' },
    { id: 'mp', label: 'Marketplace Pros' },
];

export default function BusinessInsights() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('restock');

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await ApiClient.get('/api/business-insights/dashboard');
            setData(res.data);
        } catch (err) {
            console.error('Error fetching business insights', err);
            setError('Failed to load business insights. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{error}</p>
                <button
                    onClick={fetchDashboard}
                    className="mt-2 text-sm text-red-700 underline hover:text-red-900"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Business Insights</h1>
                    {data?.summary?.dataAsOf && (
                        <p className="text-sm text-gray-500 mt-1">Data as of {data.summary.dataAsOf}</p>
                    )}
                </div>
                <button
                    onClick={fetchDashboard}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                    Refresh
                </button>
            </div>

            <SummaryCards summary={data?.summary} />

            <TopPriorities data={data?.topPriorities} />

            <div className="bg-white shadow rounded-lg">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8 px-4" aria-label="Tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === tab.id
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                                {tab.id === 'restock' && data?.restockAlerts?.length > 0 && (
                                    <span className="ml-2 bg-orange-100 text-orange-600 text-xs rounded-full px-2 py-0.5">
                                        {data.restockAlerts.length}
                                    </span>
                                )}
                                {tab.id === 'excess' && data?.excessInventory?.length > 0 && (
                                    <span className="ml-2 bg-red-100 text-red-600 text-xs rounded-full px-2 py-0.5">
                                        {data.excessInventory.length}
                                    </span>
                                )}
                                {tab.id === 'returns' && data?.highReturns?.length > 0 && (
                                    <span className="ml-2 bg-yellow-100 text-yellow-600 text-xs rounded-full px-2 py-0.5">
                                        {data.highReturns.length}
                                    </span>
                                )}
                                {tab.id === 'buybox' && data?.buyboxStatus?.length > 0 && (
                                    <span className="ml-2 bg-purple-100 text-purple-600 text-xs rounded-full px-2 py-0.5">
                                        {data.buyboxStatus.length}
                                    </span>
                                )}
                                {tab.id === 'mp' && data?.marketplaceProsBattle?.length > 0 && (
                                    <span className="ml-2 bg-blue-100 text-blue-600 text-xs rounded-full px-2 py-0.5">
                                        {data.marketplaceProsBattle.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-4">
                    {activeTab === 'restock' && <RestockAlerts data={data?.restockAlerts} />}
                    {activeTab === 'excess' && <ExcessInventory data={data?.excessInventory} />}
                    {activeTab === 'returns' && <HighReturns data={data?.highReturns} />}
                    {activeTab === 'buybox' && <BuyboxStatus data={data?.buyboxStatus} />}
                    {activeTab === 'mp' && <MarketplaceProsBattle data={data?.marketplaceProsBattle} />}
                </div>
            </div>
        </div>
    );
}
